import {useCallback, useEffect, useRef, useState} from "react";
import {DefaultValues, FieldErrors, Path, useForm, UseFormReturn} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {number, object, ObjectSchema, Schema, string} from "yup";

import {IPhoneFieldValue} from "@pg-design/phone-input-module";
import {IPerformanceDebugTrace} from "@pg-mono/performance-debug";

import {TurnstileAction} from "../../app/constants/turnstile_actions";
import {createRpLeadPerformanceDebugTrace} from "../../app/performance_debug";
import {prefetchTurnstileToken} from "../../app/turnstile";
import {validationMessages} from "../../form/utils/validation_messages";
import {addWarningNotification} from "../../notifications/utils/add_warning_notification";
import {hitGTMEcommerceAddToCart} from "../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_add_to_cart";
import {hitGTMEcommerceBeginCheckout} from "../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_begin_checkout";
import {IOfferDetailApplication} from "../../offer/types/IOfferDetailApplication";
import {IPropertyDetailApplication} from "../../property/types/IPropertyDetailApplication";
import {Country} from "../../region/types/Country";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {gtmCheckout} from "../../tracking/google_tag_manager/gtm_checkout";
import {gtmEventFormInquiry} from "../../tracking/google_tag_manager/gtm_event_form_inquiry";
import {IGTMEventLeadForm} from "../../tracking/google_tag_manager/gtm_event_form_open";
import {gtmProductAdd} from "../../tracking/google_tag_manager/gtm_product_add";
import {useHomeMatchPreferencesStorage} from "../../user/hooks/preferences/use_home_match_preferences_storage";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {getPreferencesFromLeadData} from "../../user/utils/preferences/get_preferences_from_lead_data";
import {isValidationError} from "../../utils/isValidationError";
import {getLeadFormCloseSnapshotFromFormValues} from "../tracking/amplitude/get_amplitude_lead_form_inquiry_topics_selected";
import {trackAmplitudeLeadFormError, trackAmplitudeLeadFormStart} from "../tracking/amplitude/track_amplitude_lead_form_events";
import {IApplicationFormValues} from "../types/form/IApplicationFormValues";
import {IApplicationData} from "../types/IApplicationData";
import {IApplicationReducerData} from "../types/IApplicationReducerData";
import {isLeadFormSubmitAppError} from "../utils/is_lead_form_submit_app_error";
import {getRodoDataFromApplication} from "../utils/lead/get_rodo_data_from_application";
import {useLeadData} from "./use_lead_data";
import {LeadFormCloseSnapshotRef} from "./use_lead_form_close_snapshot_ref";
import {useLeadRequiredParams} from "./use_lead_required_params";
import {useSendMainLead} from "./use_send_main_lead";

const baseFormSchema = object().shape({
    email: string().email(validationMessages.email).required(validationMessages.required),
    name: string().required(validationMessages.required),
    phone: object().shape({
        code: number().required(validationMessages.required),
        number: string().required(validationMessages.required)
    })
});

export interface IBaseDefaultValues {
    name: string;
    email: string;
    phone: IPhoneFieldValue;
    marketing_email: boolean;
    marketing_sms: boolean;
}

export interface IUseLeadFormConfig<TExtraValues extends Record<string, unknown> = Record<string, never>> {
    initialLeadFormValues: IApplicationFormValues | null;
    onSuccessfulSubmit: (
        sentApplication: IApplicationData | null,
        offer?: IOfferDetailApplication,
        property?: IPropertyDetailApplication,
        recommendedFilters?: Record<string, string>
    ) => Promise<void>;
    handleModalClose: (forceClose?: boolean) => void;
    extraDefaultValues?: TExtraValues;
    extraSchema?: ObjectSchema<Record<string, unknown>> | Record<string, unknown>;
    customSourceSection?: string;
    transformFormValues?: (values: IBaseDefaultValues & TExtraValues, defaultValues: IBaseDefaultValues & TExtraValues) => IApplicationFormValues;
    onSubmitDraft?: (values: IApplicationFormValues) => Promise<void>;
    onBeforeSubmit?: (values: IBaseDefaultValues & TExtraValues) => void;
    resetOnChange?: unknown[];
    analyticsParams?: {customSourceSection?: string};
    formCloseSnapshotRef?: LeadFormCloseSnapshotRef;
}

export interface IUseLeadFormReturn<TExtraValues extends Record<string, unknown> = Record<string, never>> {
    formMethods: UseFormReturn<IBaseDefaultValues & TExtraValues>;
    isSending: boolean;
    handleFormSubmit: ReturnType<UseFormReturn<IBaseDefaultValues & TExtraValues>["handleSubmit"]>;
    onInteraction: (formFillAction?: "autofill" | "manual_fill") => void;
    preloadTurnstileToken: () => void;
    applicationData: IApplicationReducerData | undefined;
    vendorName: string | undefined;
    offerPersonalDataProcessorName: string | undefined;
    rodoLinks: ReturnType<typeof getRodoDataFromApplication>["rodoLinks"];
}

export function useLeadForm<TExtraValues extends Record<string, unknown> = Record<string, never>>(
    config: IUseLeadFormConfig<TExtraValues>
): IUseLeadFormReturn<TExtraValues> {
    const {
        initialLeadFormValues,
        onSuccessfulSubmit,
        handleModalClose,
        extraDefaultValues,
        extraSchema,
        customSourceSection,
        transformFormValues,
        onSubmitDraft,
        onBeforeSubmit,
        resetOnChange,
        analyticsParams,
        formCloseSnapshotRef
    } = config;

    const dispatch = useAppDispatch();
    const [isSending, setIsSending] = useState(false);
    const wasInteractionHitRef = useRef(false);
    const amplitudeLeadFormStartTrackedRef = useRef(false);
    const submitTraceRef = useRef<IPerformanceDebugTrace | null>(null);
    const didPrefetchTurnstileRef = useRef(false);

    const {vendorId, offerId, propertyId, source} = useLeadRequiredParams();
    const leadModalState = useAppSelector((state) => state.mainLead);
    const amplitudeLeadFormEventTracking = useAppSelector((state) => state.mainLead.amplitudeLeadFormEventTracking);
    const applicationData = useLeadData({vendorId, offerId: offerId || undefined, propertyId: propertyId || undefined}) ?? undefined;
    const viewType = useAppSelector((state) => state.viewType.current);
    const mainLeadType = useAppSelector((state) => state.mainLead.mainLeadType);
    const isAuthenticated = useIsAuthenticated();
    const {vendorName, offerPersonalDataProcessorName, rodoLinks} = getRodoDataFromApplication(applicationData);

    const baseDefaultValues: IBaseDefaultValues = {
        name: initialLeadFormValues?.name || "",
        email: initialLeadFormValues?.email || "",
        phone: initialLeadFormValues?.phone || {code: 48, number: ""},
        marketing_email: initialLeadFormValues?.marketing_email || false,
        marketing_sms: initialLeadFormValues?.marketing_sms || false
    };

    const defaultValues = {
        ...baseDefaultValues,
        ...extraDefaultValues
    } as DefaultValues<IBaseDefaultValues & TExtraValues>;

    const gtmData: IGTMEventLeadForm = {
        clickText: leadModalState.trackingMeta.openButtonSourceText ?? "",
        source: leadModalState.trackingMeta.openButtonSource ?? "",
        vendorName: (applicationData?.vendor?.detail?.name || applicationData?.vendor?.detail?.id.toString()) ?? "",
        offerName: (applicationData?.offer?.detail?.name || offerId?.toString()) ?? ""
    };

    const resolvedExtraSchema = extraSchema
        ? extraSchema instanceof ObjectSchema
            ? extraSchema
            : object().shape(extraSchema as Record<string, Schema>)
        : null;
    const schema = resolvedExtraSchema ? baseFormSchema.concat(resolvedExtraSchema as ObjectSchema<Record<string, unknown>>) : baseFormSchema;

    const formMethods = useForm<IBaseDefaultValues & TExtraValues>({
        defaultValues: defaultValues,
        resolver: yupResolver(schema) as never,
        mode: "onChange"
    });

    const sendLead = useSendMainLead();
    const {putPreferences} = useHomeMatchPreferencesStorage();

    const sourceSection = customSourceSection ?? leadModalState.sourceSection;
    const prefetchTurnstileAction = onSubmitDraft && !isAuthenticated ? TurnstileAction.REGISTRATION : TurnstileAction.APPLICATION_CREATE;

    useEffect(() => {
        didPrefetchTurnstileRef.current = false;
    }, [prefetchTurnstileAction, sourceSection, vendorId, offerId, propertyId]);

    const preloadTurnstileToken = useCallback(() => {
        if (didPrefetchTurnstileRef.current) {
            return;
        }

        didPrefetchTurnstileRef.current = true;
        prefetchTurnstileToken(prefetchTurnstileAction);
    }, [prefetchTurnstileAction]);

    const onInteraction = (formFillAction: "autofill" | "manual_fill" = "manual_fill") => {
        preloadTurnstileToken();

        if (!wasInteractionHitRef.current && mainLeadType && sourceSection && applicationData) {
            wasInteractionHitRef.current = true;

            gtmCheckout(applicationData, viewType, mainLeadType);
            hitGTMEcommerceBeginCheckout(applicationData, viewType, mainLeadType, sourceSection, formFillAction);
        }
    };

    // Track form opening and autofill interaction
    useEffect(() => {
        if (!amplitudeLeadFormEventTracking) {
            amplitudeLeadFormStartTrackedRef.current = false;
        }

        if (!mainLeadType || !sourceSection || !applicationData) {
            return;
        }

        if (amplitudeLeadFormEventTracking && !amplitudeLeadFormStartTrackedRef.current && leadModalState.sourceSection != null) {
            const hasAutofill = !!(initialLeadFormValues?.name || initialLeadFormValues?.email || initialLeadFormValues?.phone?.number);

            amplitudeLeadFormStartTrackedRef.current = true;

            trackAmplitudeLeadFormStart({
                trackingContext: amplitudeLeadFormEventTracking,
                leadData: applicationData,
                source,
                sourceSection: leadModalState.sourceSection,
                ...(hasAutofill ? {eventAction: "autofill"} : {})
            });
        }

        gtmProductAdd(applicationData, viewType, mainLeadType);
        hitGTMEcommerceAddToCart(applicationData, viewType, mainLeadType, sourceSection);

        if (initialLeadFormValues?.name || initialLeadFormValues?.email || initialLeadFormValues?.phone?.number) {
            onInteraction("autofill");
        }
    }, [amplitudeLeadFormEventTracking, applicationData, initialLeadFormValues, leadModalState.sourceSection, mainLeadType, source, sourceSection]);

    useEffect(() => {
        if (resetOnChange) {
            formMethods.reset(defaultValues);
        }
    }, resetOnChange ?? []);

    useEffect(() => {
        if (!formCloseSnapshotRef) {
            return;
        }

        formCloseSnapshotRef.current = () => getLeadFormCloseSnapshotFromFormValues(formMethods.getValues());

        return () => {
            formCloseSnapshotRef.current = undefined;
        };
    }, [formCloseSnapshotRef, formMethods]);

    const onSubmit = async (values: IBaseDefaultValues & TExtraValues) => {
        const trace =
            submitTraceRef.current ??
            createRpLeadPerformanceDebugTrace("rp_lead_form_submit", {
                mainLeadType,
                offerId,
                propertyId,
                source,
                sourceSection,
                vendorId
            });

        trace.mark("form_validation:valid");
        onBeforeSubmit?.(values);
        trace.mark("on_before_submit:done", {hasOnBeforeSubmit: Boolean(onBeforeSubmit)});

        const formValues: IApplicationFormValues = transformFormValues
            ? transformFormValues(values, defaultValues as IBaseDefaultValues & TExtraValues)
            : {
                  name: values.name,
                  email: values.email,
                  phone: values.phone,
                  marketing_email: values.marketing_email,
                  marketing_sms: values.marketing_sms
              };
        trace.mark("form_values:prepared", {
            hasCustomTransform: Boolean(transformFormValues),
            hasMessage: Boolean(formValues.text),
            marketingEmail: Boolean(formValues.marketing_email),
            marketingSms: Boolean(formValues.marketing_sms)
        });

        setIsSending(true);
        trace.mark("submit_loader:enabled");

        try {
            if (onSubmitDraft) {
                trace.mark("submit_draft:start");
                await onSubmitDraft(formValues);
                trace.mark("submit_draft:done");
                return;
            }

            trace.mark("send_lead:start");
            const application = await sendLead(formValues, handleModalClose, {
                analyticsParams: {
                    ...(analyticsParams ?? (customSourceSection ? {customSourceSection} : {})),
                    ...(amplitudeLeadFormEventTracking
                        ? {
                              amplitudeLeadFormSubmitContext: {
                                  ...(!!(initialLeadFormValues?.name || initialLeadFormValues?.email || initialLeadFormValues?.phone?.number)
                                      ? {eventAction: "autofill" as const}
                                      : {})
                              }
                          }
                        : {})
                }
            });
            trace.mark("send_lead:done", {
                hasApplication: Boolean(application),
                userCreated: Boolean(application?.user_created)
            });

            if (applicationData && applicationData.offer?.detail?.region.country === Country.POLAND) {
                putPreferences(getPreferencesFromLeadData(applicationData));
                trace.mark("preferences_storage:updated");
            }

            trace.mark("on_successful_submit:start");
            await onSuccessfulSubmit(application || null, applicationData?.offer?.detail, applicationData?.property?.detail);
            trace.mark("on_successful_submit:done");
        } catch (error) {
            trace.mark("submit:error", {
                isLeadFormSubmitAppError: isLeadFormSubmitAppError(error),
                isValidationError: isValidationError(error)
            });

            if (
                isLeadFormSubmitAppError(error) &&
                amplitudeLeadFormEventTracking &&
                applicationData &&
                source != null &&
                leadModalState.sourceSection != null
            ) {
                trackAmplitudeLeadFormError({
                    trackingContext: amplitudeLeadFormEventTracking,
                    leadData: applicationData,
                    source,
                    sourceSection: leadModalState.sourceSection,
                    appError: error.appError
                });
            }

            if (isValidationError(error)) {
                const fieldErrors = error.appError.fieldErrors;

                for (const fieldName of Object.keys(fieldErrors)) {
                    const errorMessage = fieldErrors[fieldName];
                    formMethods.setError(fieldName as Path<IBaseDefaultValues & TExtraValues>, {
                        message: errorMessage.join(", ")
                    });
                }

                if (error.appError.nonFieldErrors.length) {
                    dispatch(addWarningNotification(error.appError.nonFieldErrors.join(" ")));
                }
            }
        } finally {
            setIsSending(false);
            trace.mark("submit_loader:disabled");

            gtmEventFormInquiry(gtmData.clickText, gtmData.source, gtmData.vendorName, gtmData.offerName);
            trace.end("gtm_form_inquiry:done");
            submitTraceRef.current = null;
        }
    };

    const onInvalidSubmit = useCallback(
        (errors: FieldErrors<IBaseDefaultValues & TExtraValues>) => {
            const fieldNames = Object.keys(errors);
            submitTraceRef.current?.end("form_validation:invalid", {fieldNames});
            submitTraceRef.current = null;

            if (!amplitudeLeadFormEventTracking || !applicationData || source == null || leadModalState.sourceSection == null) {
                return;
            }

            trackAmplitudeLeadFormError({
                trackingContext: amplitudeLeadFormEventTracking,
                leadData: applicationData,
                source,
                sourceSection: leadModalState.sourceSection,
                fieldErrors: errors,
                ctaText: gtmData.clickText || undefined
            });
        },
        [amplitudeLeadFormEventTracking, applicationData, gtmData.clickText, leadModalState.sourceSection, source]
    );

    const handleFormSubmit = useCallback<ReturnType<UseFormReturn<IBaseDefaultValues & TExtraValues>["handleSubmit"]>>(
        (event) => {
            submitTraceRef.current = createRpLeadPerformanceDebugTrace("rp_lead_form_submit", {
                mainLeadType,
                offerId,
                propertyId,
                source,
                sourceSection,
                vendorId
            });
            submitTraceRef.current.mark("submit_event:received");

            return formMethods.handleSubmit(onSubmit, onInvalidSubmit)(event);
        },
        [formMethods, mainLeadType, offerId, onInvalidSubmit, onSubmit, propertyId, source, sourceSection, vendorId]
    );

    return {
        formMethods,
        isSending,
        handleFormSubmit,
        onInteraction,
        preloadTurnstileToken,
        applicationData,
        vendorName,
        offerPersonalDataProcessorName,
        rodoLinks
    };
}
import {useCallback, useEffect, useRef, useState} from "react";
import {DefaultValues, FieldErrors, Path, useForm, UseFormReturn} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {number, object, ObjectSchema, Schema, string} from "yup";

import {IPhoneFieldValue} from "@pg-design/phone-input-module";
import {IPerformanceDebugTrace} from "@pg-mono/performance-debug";

import {TurnstileAction} from "../../app/constants/turnstile_actions";
import {createRpLeadPerformanceDebugTrace} from "../../app/performance_debug";
import {prefetchTurnstileToken} from "../../app/turnstile";
import {validationMessages} from "../../form/utils/validation_messages";
import {addWarningNotification} from "../../notifications/utils/add_warning_notification";
import {hitGTMEcommerceAddToCart} from "../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_add_to_cart";
import {hitGTMEcommerceBeginCheckout} from "../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_begin_checkout";
import {IOfferDetailApplication} from "../../offer/types/IOfferDetailApplication";
import {IPropertyDetailApplication} from "../../property/types/IPropertyDetailApplication";
import {Country} from "../../region/types/Country";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {gtmCheckout} from "../../tracking/google_tag_manager/gtm_checkout";
import {gtmEventFormInquiry} from "../../tracking/google_tag_manager/gtm_event_form_inquiry";
import {IGTMEventLeadForm} from "../../tracking/google_tag_manager/gtm_event_form_open";
import {gtmProductAdd} from "../../tracking/google_tag_manager/gtm_product_add";
import {useHomeMatchPreferencesStorage} from "../../user/hooks/preferences/use_home_match_preferences_storage";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {getPreferencesFromLeadData} from "../../user/utils/preferences/get_preferences_from_lead_data";
import {isValidationError} from "../../utils/isValidationError";
import {getLeadFormCloseSnapshotFromFormValues} from "../tracking/amplitude/get_amplitude_lead_form_inquiry_topics_selected";
import {trackAmplitudeLeadFormError, trackAmplitudeLeadFormStart} from "../tracking/amplitude/track_amplitude_lead_form_events";
import {IApplicationFormValues} from "../types/form/IApplicationFormValues";
import {IApplicationData} from "../types/IApplicationData";
import {IApplicationReducerData} from "../types/IApplicationReducerData";
import {isLeadFormSubmitAppError} from "../utils/is_lead_form_submit_app_error";
import {getRodoDataFromApplication} from "../utils/lead/get_rodo_data_from_application";
import {useLeadData} from "./use_lead_data";
import {LeadFormCloseSnapshotRef} from "./use_lead_form_close_snapshot_ref";
import {useLeadRequiredParams} from "./use_lead_required_params";
import {useSendMainLead} from "./use_send_main_lead";

const baseFormSchema = object().shape({
    email: string().email(validationMessages.email).required(validationMessages.required),
    name: string().required(validationMessages.required),
    phone: object().shape({
        code: number().required(validationMessages.required),
        number: string().required(validationMessages.required)
    })
});

export interface IBaseDefaultValues {
    name: string;
    email: string;
    phone: IPhoneFieldValue;
    marketing_email: boolean;
    marketing_sms: boolean;
}

export interface IUseLeadFormConfig<TExtraValues extends Record<string, unknown> = Record<string, never>> {
    initialLeadFormValues: IApplicationFormValues | null;
    onSuccessfulSubmit: (
        sentApplication: IApplicationData | null,
        offer?: IOfferDetailApplication,
        property?: IPropertyDetailApplication,
        recommendedFilters?: Record<string, string>
    ) => Promise<void>;
    handleModalClose: (forceClose?: boolean) => void;
    extraDefaultValues?: TExtraValues;
    extraSchema?: ObjectSchema<Record<string, unknown>> | Record<string, unknown>;
    customSourceSection?: string;
    transformFormValues?: (values: IBaseDefaultValues & TExtraValues, defaultValues: IBaseDefaultValues & TExtraValues) => IApplicationFormValues;
    onSubmitDraft?: (values: IApplicationFormValues) => Promise<void>;
    onBeforeSubmit?: (values: IBaseDefaultValues & TExtraValues) => void;
    resetOnChange?: unknown[];
    analyticsParams?: {customSourceSection?: string};
    formCloseSnapshotRef?: LeadFormCloseSnapshotRef;
}

export interface IUseLeadFormReturn<TExtraValues extends Record<string, unknown> = Record<string, never>> {
    formMethods: UseFormReturn<IBaseDefaultValues & TExtraValues>;
    isSending: boolean;
    handleFormSubmit: ReturnType<UseFormReturn<IBaseDefaultValues & TExtraValues>["handleSubmit"]>;
    onInteraction: (formFillAction?: "autofill" | "manual_fill") => void;
    preloadTurnstileToken: () => void;
    applicationData: IApplicationReducerData | undefined;
    vendorName: string | undefined;
    offerPersonalDataProcessorName: string | undefined;
    rodoLinks: ReturnType<typeof getRodoDataFromApplication>["rodoLinks"];
}

export function useLeadForm<TExtraValues extends Record<string, unknown> = Record<string, never>>(
    config: IUseLeadFormConfig<TExtraValues>
): IUseLeadFormReturn<TExtraValues> {
    const {
        initialLeadFormValues,
        onSuccessfulSubmit,
        handleModalClose,
        extraDefaultValues,
        extraSchema,
        customSourceSection,
        transformFormValues,
        onSubmitDraft,
        onBeforeSubmit,
        resetOnChange,
        analyticsParams,
        formCloseSnapshotRef
    } = config;

    const dispatch = useAppDispatch();
    const [isSending, setIsSending] = useState(false);
    const wasInteractionHitRef = useRef(false);
    const amplitudeLeadFormStartTrackedRef = useRef(false);
    const submitTraceRef = useRef<IPerformanceDebugTrace | null>(null);
    const didPrefetchTurnstileRef = useRef(false);

    const {vendorId, offerId, propertyId, source} = useLeadRequiredParams();
    const leadModalState = useAppSelector((state) => state.mainLead);
    const amplitudeLeadFormEventTracking = useAppSelector((state) => state.mainLead.amplitudeLeadFormEventTracking);
    const applicationData = useLeadData({vendorId, offerId: offerId || undefined, propertyId: propertyId || undefined}) ?? undefined;
    const viewType = useAppSelector((state) => state.viewType.current);
    const mainLeadType = useAppSelector((state) => state.mainLead.mainLeadType);
    const isAuthenticated = useIsAuthenticated();
    const {vendorName, offerPersonalDataProcessorName, rodoLinks} = getRodoDataFromApplication(applicationData);

    const baseDefaultValues: IBaseDefaultValues = {
        name: initialLeadFormValues?.name || "",
        email: initialLeadFormValues?.email || "",
        phone: initialLeadFormValues?.phone || {code: 48, number: ""},
        marketing_email: initialLeadFormValues?.marketing_email || false,
        marketing_sms: initialLeadFormValues?.marketing_sms || false
    };

    const defaultValues = {
        ...baseDefaultValues,
        ...extraDefaultValues
    } as DefaultValues<IBaseDefaultValues & TExtraValues>;

    const gtmData: IGTMEventLeadForm = {
        clickText: leadModalState.trackingMeta.openButtonSourceText ?? "",
        source: leadModalState.trackingMeta.openButtonSource ?? "",
        vendorName: (applicationData?.vendor?.detail?.name || applicationData?.vendor?.detail?.id.toString()) ?? "",
        offerName: (applicationData?.offer?.detail?.name || offerId?.toString()) ?? ""
    };

    const resolvedExtraSchema = extraSchema
        ? extraSchema instanceof ObjectSchema
            ? extraSchema
            : object().shape(extraSchema as Record<string, Schema>)
        : null;
    const schema = resolvedExtraSchema ? baseFormSchema.concat(resolvedExtraSchema as ObjectSchema<Record<string, unknown>>) : baseFormSchema;

    const formMethods = useForm<IBaseDefaultValues & TExtraValues>({
        defaultValues: defaultValues,
        resolver: yupResolver(schema) as never,
        mode: "onChange"
    });

    const sendLead = useSendMainLead();
    const {putPreferences} = useHomeMatchPreferencesStorage();

    const sourceSection = customSourceSection ?? leadModalState.sourceSection;
    const prefetchTurnstileAction = onSubmitDraft && !isAuthenticated ? TurnstileAction.REGISTRATION : TurnstileAction.APPLICATION_CREATE;

    useEffect(() => {
        didPrefetchTurnstileRef.current = false;
    }, [prefetchTurnstileAction, sourceSection, vendorId, offerId, propertyId]);

    const preloadTurnstileToken = useCallback(() => {
        if (didPrefetchTurnstileRef.current) {
            return;
        }

        didPrefetchTurnstileRef.current = true;
        prefetchTurnstileToken(prefetchTurnstileAction);
    }, [prefetchTurnstileAction]);

    const onInteraction = (formFillAction: "autofill" | "manual_fill" = "manual_fill") => {
        preloadTurnstileToken();

        if (!wasInteractionHitRef.current && mainLeadType && sourceSection && applicationData) {
            wasInteractionHitRef.current = true;

            gtmCheckout(applicationData, viewType, mainLeadType);
            hitGTMEcommerceBeginCheckout(applicationData, viewType, mainLeadType, sourceSection, formFillAction);
        }
    };

    // Track form opening and autofill interaction
    useEffect(() => {
        if (!amplitudeLeadFormEventTracking) {
            amplitudeLeadFormStartTrackedRef.current = false;
        }

        if (!mainLeadType || !sourceSection || !applicationData) {
            return;
        }

        if (amplitudeLeadFormEventTracking && !amplitudeLeadFormStartTrackedRef.current && leadModalState.sourceSection != null) {
            const hasAutofill = !!(initialLeadFormValues?.name || initialLeadFormValues?.email || initialLeadFormValues?.phone?.number);

            amplitudeLeadFormStartTrackedRef.current = true;

            trackAmplitudeLeadFormStart({
                trackingContext: amplitudeLeadFormEventTracking,
                leadData: applicationData,
                source,
                sourceSection: leadModalState.sourceSection,
                ...(hasAutofill ? {eventAction: "autofill"} : {})
            });
        }

        gtmProductAdd(applicationData, viewType, mainLeadType);
        hitGTMEcommerceAddToCart(applicationData, viewType, mainLeadType, sourceSection);

        if (initialLeadFormValues?.name || initialLeadFormValues?.email || initialLeadFormValues?.phone?.number) {
            onInteraction("autofill");
        }
    }, [amplitudeLeadFormEventTracking, applicationData, initialLeadFormValues, leadModalState.sourceSection, mainLeadType, source, sourceSection]);

    useEffect(() => {
        if (resetOnChange) {
            formMethods.reset(defaultValues);
        }
    }, resetOnChange ?? []);

    useEffect(() => {
        if (!formCloseSnapshotRef) {
            return;
        }

        formCloseSnapshotRef.current = () => getLeadFormCloseSnapshotFromFormValues(formMethods.getValues());

        return () => {
            formCloseSnapshotRef.current = undefined;
        };
    }, [formCloseSnapshotRef, formMethods]);

    const onSubmit = async (values: IBaseDefaultValues & TExtraValues) => {
        const trace =
            submitTraceRef.current ??
            createRpLeadPerformanceDebugTrace("rp_lead_form_submit", {
                mainLeadType,
                offerId,
                propertyId,
                source,
                sourceSection,
                vendorId
            });

        trace.mark("form_validation:valid");
        onBeforeSubmit?.(values);
        trace.mark("on_before_submit:done", {hasOnBeforeSubmit: Boolean(onBeforeSubmit)});

        const formValues: IApplicationFormValues = transformFormValues
            ? transformFormValues(values, defaultValues as IBaseDefaultValues & TExtraValues)
            : {
                  name: values.name,
                  email: values.email,
                  phone: values.phone,
                  marketing_email: values.marketing_email,
                  marketing_sms: values.marketing_sms
              };
        trace.mark("form_values:prepared", {
            hasCustomTransform: Boolean(transformFormValues),
            hasMessage: Boolean(formValues.text),
            marketingEmail: Boolean(formValues.marketing_email),
            marketingSms: Boolean(formValues.marketing_sms)
        });

        setIsSending(true);
        trace.mark("submit_loader:enabled");

        try {
            if (onSubmitDraft) {
                trace.mark("submit_draft:start");
                await onSubmitDraft(formValues);
                trace.mark("submit_draft:done");
                return;
            }

            trace.mark("send_lead:start");
            const application = await sendLead(formValues, handleModalClose, {
                analyticsParams: {
                    ...(analyticsParams ?? (customSourceSection ? {customSourceSection} : {})),
                    ...(amplitudeLeadFormEventTracking
                        ? {
                              amplitudeLeadFormSubmitContext: {
                                  ...(!!(initialLeadFormValues?.name || initialLeadFormValues?.email || initialLeadFormValues?.phone?.number)
                                      ? {eventAction: "autofill" as const}
                                      : {})
                              }
                          }
                        : {})
                }
            });
            trace.mark("send_lead:done", {
                hasApplication: Boolean(application),
                userCreated: Boolean(application?.user_created)
            });

            if (applicationData && applicationData.offer?.detail?.region.country === Country.POLAND) {
                putPreferences(getPreferencesFromLeadData(applicationData));
                trace.mark("preferences_storage:updated");
            }

            trace.mark("on_successful_submit:start");
            await onSuccessfulSubmit(application || null, applicationData?.offer?.detail, applicationData?.property?.detail);
            trace.mark("on_successful_submit:done");
        } catch (error) {
            trace.mark("submit:error", {
                isLeadFormSubmitAppError: isLeadFormSubmitAppError(error),
                isValidationError: isValidationError(error)
            });

            if (
                isLeadFormSubmitAppError(error) &&
                amplitudeLeadFormEventTracking &&
                applicationData &&
                source != null &&
                leadModalState.sourceSection != null
            ) {
                trackAmplitudeLeadFormError({
                    trackingContext: amplitudeLeadFormEventTracking,
                    leadData: applicationData,
                    source,
                    sourceSection: leadModalState.sourceSection,
                    appError: error.appError
                });
            }

            if (isValidationError(error)) {
                const fieldErrors = error.appError.fieldErrors;

                for (const fieldName of Object.keys(fieldErrors)) {
                    const errorMessage = fieldErrors[fieldName];
                    formMethods.setError(fieldName as Path<IBaseDefaultValues & TExtraValues>, {
                        message: errorMessage.join(", ")
                    });
                }

                if (error.appError.nonFieldErrors.length) {
                    dispatch(addWarningNotification(error.appError.nonFieldErrors.join(" ")));
                }
            }
        } finally {
            setIsSending(false);
            trace.mark("submit_loader:disabled");

            gtmEventFormInquiry(gtmData.clickText, gtmData.source, gtmData.vendorName, gtmData.offerName);
            trace.end("gtm_form_inquiry:done");
            submitTraceRef.current = null;
        }
    };

    const onInvalidSubmit = useCallback(
        (errors: FieldErrors<IBaseDefaultValues & TExtraValues>) => {
            const fieldNames = Object.keys(errors);
            submitTraceRef.current?.end("form_validation:invalid", {fieldNames});
            submitTraceRef.current = null;

            if (!amplitudeLeadFormEventTracking || !applicationData || source == null || leadModalState.sourceSection == null) {
                return;
            }

            trackAmplitudeLeadFormError({
                trackingContext: amplitudeLeadFormEventTracking,
                leadData: applicationData,
                source,
                sourceSection: leadModalState.sourceSection,
                fieldErrors: errors,
                ctaText: gtmData.clickText || undefined
            });
        },
        [amplitudeLeadFormEventTracking, applicationData, gtmData.clickText, leadModalState.sourceSection, source]
    );

    const handleFormSubmit = useCallback<ReturnType<UseFormReturn<IBaseDefaultValues & TExtraValues>["handleSubmit"]>>(
        (event) => {
            submitTraceRef.current = createRpLeadPerformanceDebugTrace("rp_lead_form_submit", {
                mainLeadType,
                offerId,
                propertyId,
                source,
                sourceSection,
                vendorId
            });
            submitTraceRef.current.mark("submit_event:received");

            return formMethods.handleSubmit(onSubmit, onInvalidSubmit)(event);
        },
        [formMethods, mainLeadType, offerId, onInvalidSubmit, onSubmit, propertyId, source, sourceSection, vendorId]
    );

    return {
        formMethods,
        isSending,
        handleFormSubmit,
        onInteraction,
        preloadTurnstileToken,
        applicationData,
        vendorName,
        offerPersonalDataProcessorName,
        rodoLinks
    };
}
